import { PropsWithChildren, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useCollectionsTree, useDrawer, useWorkspace, useWorkspaceCreditsAvailable, useWorkspacesList, useWorkspaceTasksQuota } from '../../hooks'
import { useDrawerWidth } from '../../hooks/use-drawer-width'
import { DrawerCollapsed } from './subcomponents/DrawerCollapsed'
import { DrawerCollectionsMenu } from './subcomponents/DrawerCollectionsMenu'
import { DrawerCollectionsTree } from './subcomponents/DrawerCollectionsTree'
import { DrawerHeader } from './subcomponents/DrawerHeader'
import { DrawerResizeHandler } from './subcomponents/DrawerResizeHandler'
import { DrawerSearchCollectionsInput } from './subcomponents/DrawerSearchCollectionsInput'
import { DrawerUsage } from './subcomponents/DrawerUsage'
import { DrawerWorkspacesSection } from './subcomponents/DrawerWorkspacesSection'
import { GlobeIcon } from 'lucide-react'

const DEFAULT_WIDTH = 320
const MIN_WIDTH = 250
const MAX_WIDTH = 450

export type DrawerProps = PropsWithChildren<{ id: string }>

export function Drawer (props: DrawerProps) {
  const { children, id } = props

  const [searchCollectionName, setSearchCollectionName] = useState('')
  const params = useParams()
  const navigate = useNavigate()
  const workspace = params.workspace!
  const getCollectionsPage = useCollectionsTree()
  const getWorkspace = useWorkspace(workspace)
  const getWorkspacesList = useWorkspacesList()
  const currentWorkspace = getWorkspace.data
  const getWorkspaceCreditsUsage = useWorkspaceCreditsAvailable(workspace)
  const workspaceCreditsUsage = getWorkspaceCreditsUsage.data
  const getWorkspaceTasksQuota = useWorkspaceTasksQuota(workspace)
  const workspaceTasksQuota = getWorkspaceTasksQuota.data
  const { open, setOpen } = useDrawer()
  const { width, handleResizeFieldDrawer, onStopResizeFieldDrawer } = useDrawerWidth('drawer-width', DEFAULT_WIDTH, MIN_WIDTH)

  const calculatedWidth = width >= MAX_WIDTH ? MAX_WIDTH : width

  return (
    <div className="drawer drawer-open">
      <input id={id} type="checkbox" className="drawer-toggle" />
      <div className="drawer-content border-l">
        {children}
      </div>
      <div className="drawer-side" style={{ scrollbarWidth: 'thin' }}>
        <label htmlFor={id} aria-label="close sidebar" className="drawer-overlay"></label>
        <div className="h-full flex flew-row">
          {!open && currentWorkspace && (
            <DrawerCollapsed
              workspace={currentWorkspace}
              collections={getCollectionsPage.data?.data ?? []}
              workspaces={getWorkspacesList.data}
              workspacesLoading={getWorkspacesList.isLoading}
              collectionsLoading={getCollectionsPage.isLoading}
              onCollectionsChange={() => { getCollectionsPage.refetch() }}
            />
          )}

          {open && (
            <div
              className="h-full relative flex flex-col pt-6"
              style={{ width: calculatedWidth, minWidth: MIN_WIDTH }}
            >
              <div className="overflow-y-auto grow flex flex-col px-3">
                <div className='flex flex-col gap-3 pb-3'>
                  <DrawerHeader />
                  <DrawerWorkspacesSection
                    drawerWidth={calculatedWidth}
                    workspaces={getWorkspacesList.data}
                    loading={getWorkspacesList.isLoading}
                    onSettingsClick={() => {
                      setOpen?.(false)
                      navigate(`/${workspace}/settings`)
                    }}
                  />
                  <ul className="menu w-full px-0">
                    <li>
                      <Link to={`/${workspace}/templates`}>
                        <GlobeIcon size={16} />
                        Templates
                      </Link>
                    </li>
                  </ul>
                  <DrawerSearchCollectionsInput
                    searchCollectionName={searchCollectionName}
                    onCollectionNameChange={name => setSearchCollectionName(name)}
                  />
                  <DrawerCollectionsTree
                    workspace={workspace}
                    onCollectionsChange={() => { getCollectionsPage.refetch() }}
                  />
                </div>
                <div className='overflow-y-auto' style={{ scrollbarWidth: 'thin' }}>
                  <DrawerCollectionsMenu
                    workspace={workspace}
                    collections={getCollectionsPage.data?.data ?? []}
                    searchCollectionName={searchCollectionName}
                    isLoading={getCollectionsPage.isLoading}
                    onCollectionsChange={() => { getCollectionsPage.refetch() }}
                  />
                </div>
              </div>
              <DrawerUsage
                tasksQuota={workspaceTasksQuota}
                creditsUsage={workspaceCreditsUsage}
                loading={getWorkspaceTasksQuota.isLoading || getWorkspaceCreditsUsage.isLoading}
                error={getWorkspaceCreditsUsage.error || getWorkspaceTasksQuota.error}
                onRefetch={() => {
                  getWorkspaceTasksQuota.refetch()
                  getWorkspaceCreditsUsage.refetch()
                }}
              />
            </div>
          )}

          {open && (
            <DrawerResizeHandler
              workspace={workspace}
              onDrag={change => handleResizeFieldDrawer(change)}
              onDragStop={onStopResizeFieldDrawer}
            />
          )}
        </div>
      </div>
    </div>
  )
}
